import React, { useState, useEffect } from 'react';
import { 
  PieChart, 
  TrendingUp, 
  TrendingDown, 
  Globe, 
  MessageCircle,
  Eye,
  ArrowUpRight, 
  ArrowDownRight,
  Filter,
  Download,
  Calendar,
  Hash,
  Radio,
  Bookmark,
  Heart,
  Search,
  Megaphone,
  Volume2
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import PageHeading from '../../components/PageHeading';
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';

// Örnek veriler
const shareOfVoiceData = [
  { name: 'Your Brand', share: 35, mentions: 24500, sentiment: 78, color: '#3b82f6' },
  { name: 'Competitor A', share: 25, mentions: 17500, sentiment: 65, color: '#f43f5e' },
  { name: 'Competitor B', share: 18, mentions: 12600, sentiment: 72, color: '#10b981' },
  { name: 'Competitor C', share: 12, mentions: 8400, sentiment: 60, color: '#f59e0b' },
  { name: 'Others', share: 10, mentions: 7000, sentiment: 55, color: '#8b5cf6' },
];

const trendData = [
  { month: 'Jan', yourBrand: 28, competitorA: 25, competitorB: 16, competitorC: 13 },
  { month: 'Feb', yourBrand: 29, competitorA: 25, competitorB: 17, competitorC: 12 },
  { month: 'Mar', yourBrand: 31, competitorA: 24, competitorB: 18, competitorC: 12 },
  { month: 'Apr', yourBrand: 32, competitorA: 23, competitorB: 18, competitorC: 13 },
  { month: 'May', yourBrand: 33, competitorA: 24, competitorB: 17, competitorC: 12 },
  { month: 'Jun', yourBrand: 34, competitorA: 24, competitorB: 18, competitorC: 11 },
  { month: 'Jul', yourBrand: 35, competitorA: 25, competitorB: 18, competitorC: 12 },
];

const platformData = [
  { platform: 'Social Media', yourShare: 38, totalMentions: 12500, growth: 7 },
  { platform: 'News', yourShare: 32, totalMentions: 5400, growth: 3 },
  { platform: 'Blogs', yourShare: 41, totalMentions: 3800, growth: 9 },
  { platform: 'Forums', yourShare: 28, totalMentions: 2100, growth: -2 },
  { platform: 'Review Sites', yourShare: 35, totalMentions: 700, growth: 5 },
];

const topicData = [
  { name: 'Product Quality', yourBrand: 85, competitorA: 72, competitorB: 65, competitorC: 58 },
  { name: 'Customer Service', yourBrand: 75, competitorA: 68, competitorB: 80, competitorC: 62 },
  { name: 'Price Value', yourBrand: 65, competitorA: 80, competitorB: 70, competitorC: 75 },
  { name: 'Innovation', yourBrand: 88, competitorA: 70, competitorB: 75, competitorC: 60 },
  { name: 'User Experience', yourBrand: 82, competitorA: 75, competitorB: 68, competitorC: 65 },
];

const sentimentData = [
  { name: 'Your Brand', positive: 65, neutral: 25, negative: 10 },
  { name: 'Competitor A', positive: 48, neutral: 32, negative: 20 },
  { name: 'Competitor B', positive: 58, neutral: 27, negative: 15 },
  { name: 'Competitor C', positive: 42, neutral: 35, negative: 23 },
];

const trendsInsights = [
  { topic: 'Eco-Friendly Products', mentions: 3420, growth: 28, relevance: 'High' },
  { topic: 'Customer Support', mentions: 2850, growth: -5, relevance: 'Medium' },
  { topic: 'Product Durability', mentions: 4120, growth: 12, relevance: 'High' },
  { topic: 'Mobile App Experience', mentions: 1980, growth: 35, relevance: 'High' },
  { topic: 'Pricing Concerns', mentions: 2340, growth: 8, relevance: 'Medium' },
];

const topHashtags = [
  { tag: '#sustainability', count: 4250, growth: 32 },
  { tag: '#innovation', count: 3890, growth: 18 },
  { tag: '#customerexperience', count: 3650, growth: 22 },
  { tag: '#qualitymatters', count: 3100, growth: 15 },
  { tag: '#industrytrends', count: 2950, growth: -3 },
];

const mentionsBySource = [
  { name: 'Twitter', value: 32, color: '#1DA1F2' },
  { name: 'Facebook', value: 18, color: '#4267B2' },
  { name: 'Instagram', value: 16, color: '#E1306C' },
  { name: 'Reddit', value: 12, color: '#FF4500' },
  { name: 'LinkedIn', value: 10, color: '#0A66C2' },
  { name: 'News Sites', value: 8, color: '#6B7280' },
  { name: 'Blogs', value: 4, color: '#10B981' },
];

const ShareOfVoiceDashboard = () => {
  const [timeRange, setTimeRange] = useState('quarter');
  const [platform, setPlatform] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Veri yükleme simülasyonu
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US', {
      notation: value >= 10000 ? "compact" : "standard",
      compactDisplay: "short"
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Dashboard Header - Inline page heading */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Share of Voice Analysis</h1>
          <p className="mt-1 text-sm text-gray-500">Analyze your brand's visibility and mentions compared to competitors</p>
        </div>
        <div className="mt-4 sm:mt-0">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-white rounded-xl border border-gray-200 p-1 shadow-sm">
              <button className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${timeRange === 'month' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-50'}`} onClick={() => setTimeRange('month')}>Month</button>
              <button className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${timeRange === 'quarter' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-50'}`} onClick={() => setTimeRange('quarter')}>Quarter</button>
              <button className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${timeRange === 'year' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-50'}`} onClick={() => setTimeRange('year')}>Year</button>
            </div>
            <Button className="flex items-center gap-2 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 rounded-xl">
              <Filter size={16} />
              <span>Filters</span>
            </Button>
            <Button className="flex items-center gap-2 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 rounded-xl">
              <Download size={16} />
              <span>Export</span>
            </Button>
          </div>
        </div>
      </div>

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 mt-6">
        <Card className="p-5 rounded-xl">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Share of Voice</p>
              <h3 className="text-3xl font-bold mt-2 text-gray-800">35%</h3>
              <div className="flex items-center mt-2 text-emerald-600">
                <ArrowUpRight size={16} />
                <span className="text-sm font-medium ml-1">+3.2% vs last period</span>
              </div>
            </div>
            <div className="flex items-center justify-center w-12 h-12 bg-indigo-100 text-indigo-600 rounded-xl">
              <Volume2 size={20} />
            </div>
          </div>
        </Card>

        <Card className="p-5 rounded-xl">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Mentions</p>
              <h3 className="text-3xl font-bold mt-2 text-gray-800">24.5K</h3>
              <div className="flex items-center mt-2 text-emerald-600">
                <ArrowUpRight size={16} />
                <span className="text-sm font-medium ml-1">+12.8% vs last period</span>
              </div>
            </div>
            <div className="flex items-center justify-center w-12 h-12 bg-blue-100 text-blue-600 rounded-xl">
              <MessageCircle size={20} />
            </div>
          </div>
        </Card>

        <Card className="p-5 rounded-xl">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Audience Reach</p>
              <h3 className="text-3xl font-bold mt-2 text-gray-800">5.2M</h3>
              <div className="flex items-center mt-2 text-emerald-600">
                <ArrowUpRight size={16} />
                <span className="text-sm font-medium ml-1">+8.5% vs last period</span>
              </div>
            </div>
            <div className="flex items-center justify-center w-12 h-12 bg-rose-100 text-rose-600 rounded-xl">
              <Eye size={20} />
            </div>
          </div>
        </Card>

        <Card className="p-5 rounded-xl">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Sentiment Score</p>
              <h3 className="text-3xl font-bold mt-2 text-gray-800">78</h3>
              <div className="flex items-center mt-2 text-rose-600">
                <ArrowDownRight size={16} />
                <span className="text-sm font-medium ml-1">-1.2 vs last period</span>
              </div>
            </div>
            <div className="flex items-center justify-center w-12 h-12 bg-emerald-100 text-emerald-600 rounded-xl">
              <Heart size={20} />
            </div>
          </div>
        </Card>
      </div>

      {/* Share of Voice Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Card className="lg:col-span-2 rounded-xl overflow-hidden">
          <div className="p-5 border-b border-gray-100">
            <h2 className="font-semibold text-lg text-gray-800">Share of Voice Trend</h2>
          </div>
          <div className="p-5">
            <div className="flex justify-end mb-4">
              <Button className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm h-8 px-3 rounded-xl">
                <Calendar size={14} className="mr-1" />
                Last 6 Months
              </Button>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(value) => `${value}%`} />
                <Tooltip
                  formatter={(value) => [`${value}%`, 'Share of Voice']}
                  labelFormatter={(label) => `Month: ${label}`}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="yourBrand"
                  name="Your Brand"
                  fill="#3b82f6"
                  stroke="#3b82f6"
                  fillOpacity={0.2}
                  strokeWidth={3}
                />
                <Area
                  type="monotone"
                  dataKey="competitorA"
                  name="Competitor A"
                  fill="#f43f5e"
                  stroke="#f43f5e"
                  fillOpacity={0.1}
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="competitorB"
                  name="Competitor B"
                  fill="#10b981"
                  stroke="#10b981"
                  fillOpacity={0.1}
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="competitorC"
                  name="Competitor C"
                  fill="#f59e0b"
                  stroke="#f59e0b"
                  fillOpacity={0.1}
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="rounded-xl overflow-hidden">
          <div className="p-5 border-b border-gray-100">
            <h2 className="font-semibold text-lg text-gray-800">Current Voice Distribution</h2>
          </div>
          <div className="p-5">
            <ResponsiveContainer width="100%" height={300}>
              <RechartsPieChart>
                <Pie
                  data={shareOfVoiceData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={3}
                  dataKey="share"
                  nameKey="name"
                  label={({ name, share }) => `${name}: ${share}%`}
                  labelLine={false}
                >
                  {shareOfVoiceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value}%`, 'Share of Voice']} />
                <Legend
                  layout="vertical"
                  verticalAlign="middle"
                  align="right"
                />
              </RechartsPieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Platform Distribution */}
      <Card className="mb-8 rounded-xl overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-lg text-gray-800">Platform Analysis</h2>
          <Select
            className="w-48 rounded-xl"
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
          >
            <option value="all">All Platforms</option>
            <option value="social">Social Media</option>
            <option value="news">News</option>
            <option value="blogs">Blogs</option>
            <option value="forums">Forums</option>
            <option value="reviews">Review Sites</option>
          </Select>
        </div>
        <div className="p-5">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Your Share</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Mentions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">YoY Growth</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {platformData.map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {item.platform === 'Social Media' && <MessageCircle className="w-5 h-5 text-blue-400 mr-3" />}
                        {item.platform === 'News' && <Globe className="w-5 h-5 text-gray-400 mr-3" />}
                        {item.platform === 'Blogs' && <Radio className="w-5 h-5 text-green-400 mr-3" />}
                        {item.platform === 'Forums' && <MessageCircle className="w-5 h-5 text-purple-400 mr-3" />}
                        {item.platform === 'Review Sites' && <Bookmark className="w-5 h-5 text-red-400 mr-3" />}
                        <div className="font-medium text-gray-900">{item.platform}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-gray-900">{item.yourShare}%</div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                        <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${item.yourShare}%` }}></div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-900">{formatNumber(item.totalMentions)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.growth > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {item.growth > 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                        {item.growth > 0 ? '+' : ''}{item.growth}%
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        item.yourShare > 35 ? 'bg-green-100 text-green-800' : 
                        item.yourShare > 30 ? 'bg-blue-100 text-blue-800' : 
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {item.yourShare > 35 ? 'Dominant' : item.yourShare > 30 ? 'Strong' : 'Developing'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      {/* Topic Analysis & Top Hashtags */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="rounded-xl overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-lg text-gray-800">Topic Analysis</h2>
            <Button className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm h-8 px-3 rounded-xl">
              <Filter size={14} className="mr-1" />
              Filter Topics
            </Button>
          </div>
          <div className="p-5">
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart outerRadius={90} data={topicData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="name" />
                <PolarRadiusAxis angle={30} domain={[0, 100]} />
                <Radar name="Your Brand" dataKey="yourBrand" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} />
                <Radar name="Competitor A" dataKey="competitorA" stroke="#f43f5e" fill="#f43f5e" fillOpacity={0.3} />
                <Radar name="Competitor B" dataKey="competitorB" stroke="#10b981" fill="#10b981" fillOpacity={0.3} />
                <Radar name="Competitor C" dataKey="competitorC" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.3} />
                <Legend />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="rounded-xl overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-lg text-gray-800">Top Hashtags</h2>
            <Button className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm h-8 px-3 rounded-xl">
              <Hash size={14} className="mr-1" />
              View All
            </Button>
          </div>
          <div className="p-5">
            <div className="space-y-4">
              {topHashtags.map((hashtag, index) => (
                <div key={index} className="flex items-center justify-between py-2">
                  <div className="flex items-center">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 text-blue-600 mr-3">
                      <Hash size={16} />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{hashtag.tag}</div>
                      <div className="text-xs text-gray-500">{formatNumber(hashtag.count)} mentions</div>
                    </div>
                  </div>
                  <div className={`flex items-center ${hashtag.growth > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {hashtag.growth > 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                    <span className="ml-1 text-xs font-semibold">
                      {hashtag.growth > 0 ? '+' : ''}{hashtag.growth}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      {/* Sentiment Analysis */}
      <Card className="mb-8 rounded-xl overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-lg text-gray-800">Sentiment Analysis</h2>
          <Button className="bg-blue-500 text-white hover:bg-blue-600 text-sm rounded-xl">
            Detailed Sentiment Report
          </Button>
        </div>
        <div className="p-5">
          <div className="overflow-x-auto">
            <ResponsiveContainer width="100%" height={300}>
              <RechartsBarChart data={sentimentData} layout="vertical" barSize={20}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={(value) => `${value}%`} />
                <YAxis dataKey="name" type="category" width={100} />
                <Tooltip formatter={(value) => [`${value}%`, '']} />
                <Legend />
                <Bar dataKey="positive" name="Positive" stackId="a" fill="#10b981" />
                <Bar dataKey="neutral" name="Neutral" stackId="a" fill="#6b7280" />
                <Bar dataKey="negative" name="Negative" stackId="a" fill="#f43f5e" />
              </RechartsBarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Card>

      {/* Mentions by Source & Emerging Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="rounded-xl overflow-hidden">
          <div className="p-5 border-b border-gray-100">
            <h2 className="font-semibold text-lg text-gray-800">Mentions by Source</h2>
          </div>
          <div className="p-5">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={mentionsBySource}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={90}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {mentionsBySource.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value}%`, 'of total mentions']} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="rounded-xl overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-lg text-gray-800">Emerging Trends</h2>
            <Button className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm h-8 px-3 rounded-xl">
              <Search size={14} className="mr-1" />
              Explore More
            </Button>
          </div>
          <div className="p-5">
            <div className="space-y-4">
              {trendsInsights.map((trend, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium text-gray-900">{trend.topic}</div>
                      <div className="text-sm text-gray-500 mt-1">{formatNumber(trend.mentions)} mentions</div>
                    </div>
                    <div className={`flex items-center ${trend.growth > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {trend.growth > 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                      <span className="ml-1 font-medium text-sm">
                        {trend.growth > 0 ? '+' : ''}{trend.growth}%
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center mt-2">
                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                      trend.relevance === 'High' ? 'bg-emerald-100 text-emerald-800' :
                      trend.relevance === 'Medium' ? 'bg-amber-100 text-amber-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {trend.relevance} Relevance
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      {/* Action Recommendations */}
      <Card className="rounded-xl overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-center">
          <div className="flex items-center justify-center w-10 h-10 bg-indigo-100 text-indigo-600 rounded-xl mr-4">
            <Megaphone size={20} />
          </div>
          <div>
            <h2 className="font-semibold text-lg text-gray-800">Strategic Recommendations</h2>
            <p className="text-gray-500 text-sm">Based on your share of voice analysis and competitor positioning</p>
          </div>
        </div>
        
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 border border-gray-100 rounded-xl bg-blue-50">
              <h3 className="font-medium text-blue-700 mb-2">Content Strategy</h3>
              <p className="text-gray-600 text-sm">Leverage your strong position in 'Innovation' topics by creating more thought leadership content focused on industry innovations.</p>
            </div>
            
            <div className="p-4 border border-gray-100 rounded-xl bg-emerald-50">
              <h3 className="font-medium text-emerald-700 mb-2">Social Media Focus</h3>
              <p className="text-gray-600 text-sm">Increase Twitter engagement where you have 32% share of voice by participating in trending topics with #sustainability hashtag.</p>
            </div>
            
            <div className="p-4 border border-gray-100 rounded-xl bg-amber-50">
              <h3 className="font-medium text-amber-700 mb-2">Competitive Edge</h3>
              <p className="text-gray-600 text-sm">Address 'Price Value' perception where Competitor A is outperforming you by highlighting your product's quality-to-price advantages.</p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ShareOfVoiceDashboard; 